import React, { useState, useEffect, useContext } from 'react';
import {
  Typography,
  Paper,
  Box,
  CircularProgress,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Snackbar,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  getAccountsPayable, 
  markAPPaymentComplete, 
  getEmiInstallments, 
  updateInstallmentStatus,
  getOrganizationPaymentHistory,
  getCompletedAccountsPayable,
  toggleInstallmentPaymentStatus
} from '../services/orderService';
import { getSuppliers } from '../services/supplierService';
import { OrgContext } from '../contexts/OrgContext';
import { CurrencyContext } from '../contexts/CurrencyContext';
import formatDateToStandard from '../utils/DateFormatter';
import { Check, Launch, Receipt, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import BackButton from '../components/BackButton';
import EmiInstallmentTable from '../components/EmiInstallmentTable';
import TanStackTable from '../components/TanStackTable';
import CurrencyFormatter from '../utils/CurrencyFormatter';

const AccountsPayable = () => {
  const [loading, setLoading] = useState(true);
  const [pendingAP, setPendingAP] = useState([]);
  const [completedAP, setCompletedAP] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [paymentHistoryLoading, setPaymentHistoryLoading] = useState(false);
  const [paymentHistoryPagination, setPaymentHistoryPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false
  });
  const [completedAPLoading, setCompletedAPLoading] = useState(false);
  const [completedAPPagination, setCompletedAPPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false
  });
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });
  const [emiInstallments, setEmiInstallments] = useState([]);
  const { orgId } = useContext(OrgContext);
  const { currency } = useContext(CurrencyContext);
  const navigate = useNavigate();
  const { supplierId } = useParams();

  const currencySymbols = {
    USD: '$', EUR: '€', GBP: '£', AUD: 'A$', CAD: 'C$',
    CHF: 'CHF', CNY: '¥', INR: '₹', JPY: '¥', BDT: '৳',
  };
  const symbol = currencySymbols[currency] || currency;

  // Fetch data
  const fetchData = async () => {
    setLoading(true);
    try {
      // If supplierId is provided in URL params, use it
      const supplierIdToUse = supplierId || selectedSupplier || null;
      const data = await getAccountsPayable(supplierIdToUse);
      
      // Split into pending and completed
      const pending = data.filter(item => !item.meta?.ap_complete);
      const completed = data.filter(item => item.meta?.ap_complete);
      
      // Sort pending by payment due date (ascending)
      pending.sort((a, b) => new Date(a.payment_due_date) - new Date(b.payment_due_date));
      
      // Sort completed by completion date (descending)
      completed.sort((a, b) => {
        if (!a.meta?.ap_completion_date) return 1;
        if (!b.meta?.ap_completion_date) return -1;
        return new Date(b.meta.ap_completion_date) - new Date(a.meta.ap_completion_date);
      });
      
      setPendingAP(pending);
      setCompletedAP(completed);
    } catch (error) {
      console.error('Error fetching accounts payable:', error);
      setAlert({
        open: true,
        message: 'Failed to load accounts payable data',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchEmiInstallments = async () => {
    try {
      const supplierId = selectedSupplier || null;
      const filters = { supplierId, orderType: 'Purchase' };
      const data = await getEmiInstallments(orgId, filters);
      setEmiInstallments(data);
    } catch (error) {
      console.error('Error fetching EMI installments:', error);
      setAlert({
        open: true,
        message: 'Failed to fetch EMI installments',
        severity: 'error'
      });
    }
  };

  // Fetch suppliers
  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        const response = await getSuppliers(orgId);
        setSuppliers(response.data);
        
        // If supplierId is in URL params, set it as selected
        if (supplierId) {
          setSelectedSupplier(supplierId);
        }
      } catch (error) {
        console.error('Error fetching suppliers:', error);
      }
    };
    
    if (orgId) {
      fetchSuppliers();
    }
  }, [orgId, supplierId]);

  // Fetch AP data when selected supplier changes
  useEffect(() => {
    if (orgId) {
      fetchData();
      fetchEmiInstallments();
      if (tabValue === 0) {
        fetchCompletedAP();
      }
    }
  }, [orgId, selectedSupplier, supplierId]);

  useEffect(() => {
    if (tabValue === 0) {
      fetchCompletedAP();
    }
  }, [tabValue]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    if (newValue === 2) {
      fetchPaymentHistory();
    }
  };

  const fetchPaymentHistory = async (options = {}) => {
    try {
      setPaymentHistoryLoading(true);
      const response = await getOrganizationPaymentHistory({
        page: options.page || paymentHistoryPagination.page,
        limit: options.limit || paymentHistoryPagination.limit,
        search: options.search,
        sortBy: options.sortBy || 'ph.payment_date',
        sortOrder: options.sortOrder || 'DESC',
        payment_type: 'ap'
      });
      
      setPaymentHistory(response.data || []);
      setPaymentHistoryPagination(response.pagination || paymentHistoryPagination);
    } catch (error) {
      console.error('Error fetching payment history:', error);
      setAlert({
        open: true,
        message: 'Failed to fetch payment history',
        severity: 'error'
      });
    } finally {
      setPaymentHistoryLoading(false);
    }
  };

  const fetchCompletedAP = async (options = {}) => {
    try {
      setCompletedAPLoading(true);
      const response = await getCompletedAccountsPayable({
        page: options.page || completedAPPagination.page,
        limit: options.limit || completedAPPagination.limit,
        search: options.search,
        sortBy: options.sortBy || 'ord.order_date',
        sortOrder: options.sortOrder || 'DESC',
        supplier_id: selectedSupplier || undefined
      });
      
      setCompletedAP(response.data || []);
      setCompletedAPPagination(response.pagination || completedAPPagination);
    } catch (error) {
      console.error('Error fetching completed AP:', error);
      setAlert({
        open: true,
        message: 'Failed to fetch completed accounts payable',
        severity: 'error'
      });
    } finally {
      setCompletedAPLoading(false);
    }
  };

  const handleOpenPaymentDialog = (order) => {
    setSelectedOrder(order);
    setPaymentAmount('');
    setPaymentDialogOpen(true);
  };

  const handleClosePaymentDialog = () => {
    setPaymentDialogOpen(false);
    setSelectedOrder(null);
    setPaymentAmount('');
  };

  const calculateRemainingBalance = (order) => {
    const totalPayable = parseFloat(order.accounts_payable || 0);
    const paidAmount = parseFloat(order.paid_amount || 0);
    return totalPayable - paidAmount;
  };

  const handleSubmitPayment = async () => {
    if (!selectedOrder || !paymentAmount) return;

    const amount = parseFloat(paymentAmount);
    const remainingBalance = calculateRemainingBalance(selectedOrder);

    if (amount <= 0) {
      setAlert({
        open: true,
        message: 'Payment amount must be greater than 0',
        severity: 'error'
      });
      return;
    }

    if (amount > remainingBalance) {
      setAlert({
        open: true,
        message: 'Payment amount cannot exceed remaining balance',
        severity: 'error'
      });
      return;
    }

    try {
      await markAPPaymentComplete(selectedOrder.id, amount);
      setAlert({
        open: true,
        message: 'Payment recorded successfully!',
        severity: 'success'
      });
      handleClosePaymentDialog();
      fetchData();
      if (tabValue === 2) {
        fetchPaymentHistory();
      }
    } catch (error) {
      console.error('Error recording payment:', error);
      setAlert({
        open: true,
        message: 'Failed to record payment',
        severity: 'error'
      });
    }
  };

  const isValidPayment = () => {
    if (!selectedOrder || !paymentAmount) return false;
    const amount = parseFloat(paymentAmount);
    const remainingBalance = calculateRemainingBalance(selectedOrder);
    return amount > 0 && amount <= remainingBalance;
  };

  const paymentHistoryColumns = [
    {
      accessorKey: 'payment_date',
      header: 'Payment Date',
      cell: ({ getValue }) => formatDateToStandard(getValue()),
    },
    {
      accessorKey: 'order_id',
      header: 'Order ID',
      cell: ({ getValue }) => (
        <Button
          variant="text"
          onClick={() => navigate(`/${orgId}/orders/${getValue()}`)}
          sx={{ 
            textTransform: 'none', 
            p: 0, 
            minWidth: 'auto',
            color: 'text.primary',
            '&:hover': {
              textDecoration: 'underline',
              backgroundColor: 'transparent'
            }
          }}
        >
          {getValue()}
        </Button>
      ),
    },
    {
      accessorKey: 'supplier_name',
      header: 'Supplier',
      cell: ({ row }) => {
        const supplierId = row.original.order?.supplier_id;
        const supplierName = row.original.supplier?.supplier_name || 
                            suppliers.find(s => s.id === supplierId)?.supplier_name || 
                            'N/A';
        return supplierId ? (
          <Button
            variant="text"
            onClick={() => navigate(`/${orgId}/suppliers/${supplierId}`)}
            sx={{ 
              textTransform: 'none', 
              p: 0, 
              minWidth: 'auto',
              color: 'text.primary',
              '&:hover': {
                textDecoration: 'underline',
                backgroundColor: 'transparent'
              }
            }}
          >
            {supplierName}
          </Button>
        ) : supplierName;
      },
    },
    {
      accessorKey: 'amount',
      header: 'Amount',
      cell: ({ getValue }) => <CurrencyFormatter value={getValue()} />,
    },
    {
      accessorKey: 'is_partial',
      header: 'Payment Type',
      cell: ({ getValue }) => (
        <Chip 
          label={getValue() ? 'Partial' : 'Full'} 
          size="small"
          color={getValue() ? 'warning' : 'success'}
          variant="outlined"
        />
      ),
    },
  ];

  const completedAPColumns = [
    {
      accessorKey: 'id',
      header: 'Order ID',
      cell: ({ getValue }) => (
        <Button
          variant="text"
          onClick={() => navigate(`/${orgId}/orders/${getValue()}`)}
          sx={{ 
            textTransform: 'none', 
            p: 0, 
            minWidth: 'auto',
            color: 'text.primary',
            '&:hover': {
              textDecoration: 'underline',
              backgroundColor: 'transparent'
            }
          }}
        >
          {getValue()}
        </Button>
      ),
    },
    {
      accessorKey: 'supplier_name',
      header: 'Supplier',
      cell: ({ row }) => {
        const supplierId = row.original.supplier_id;
        const supplierName = row.original.supplier?.supplier_name || 
                            suppliers.find(s => s.id === supplierId)?.supplier_name || 
                            'N/A';
        return supplierId ? (
          <Button
            variant="text"
            onClick={() => navigate(`/${orgId}/suppliers/${supplierId}`)}
            sx={{ 
              textTransform: 'none', 
              p: 0, 
              minWidth: 'auto',
              color: 'text.primary',
              '&:hover': {
                textDecoration: 'underline',
                backgroundColor: 'transparent'
              }
            }}
          >
            {supplierName}
          </Button>
        ) : supplierName;
      },
    },
    {
      accessorKey: 'order_date',
      header: 'Order Date',
      cell: ({ getValue }) => formatDateToStandard(getValue()),
    },
    {
      accessorKey: 'payment_due_date',
      header: 'Due Date',
      cell: ({ getValue }) => formatDateToStandard(getValue()),
    },
    {
      accessorKey: 'completion_date',
      header: 'Completed Date',
      cell: ({ row }) => formatDateToStandard(row.original.meta?.ap_completion_date),
    },
    {
      accessorKey: 'accounts_payable',
      header: 'Amount',
      cell: ({ getValue }) => <CurrencyFormatter value={getValue()} />,
    },
  ];

  // Handle supplier filter change
  const handleSupplierChange = (event) => {
    const value = event.target.value;
    setSelectedSupplier(value);
    
    // Update URL to reflect selected supplier
    if (value) {
      navigate(`/${orgId}/accounts-payable/${value}`);
    } else {
      navigate(`/${orgId}/accounts-payable`);
    }
  };

  // Handle marking AP as complete
  const handleMarkComplete = async (orderId) => {
    try {
      await markAPPaymentComplete(orderId);
      setAlert({
        open: true,
        message: 'Payment marked as complete successfully',
        severity: 'success'
      });
      fetchData(); // Refresh data
    } catch (error) {
      console.error('Error marking payment as complete:', error);
      setAlert({
        open: true,
        message: 'Failed to mark payment as complete',
        severity: 'error'
      });
    }
  };

  const handleInstallmentStatusUpdate = async (installmentId, statusData) => {
    try {
      await updateInstallmentStatus(installmentId, statusData);
      setAlert({
        open: true,
        message: 'Installment status updated successfully',
        severity: 'success'
      });
      fetchEmiInstallments();
    } catch (error) {
      console.error('Error updating installment status:', error);
      setAlert({
        open: true,
        message: 'Failed to update installment status',
        severity: 'error'
      });
    }
  };

  const handleToggleInstallmentPaymentStatus = async (installmentId) => {
    try {
      await toggleInstallmentPaymentStatus(installmentId);
      setAlert({
        open: true,
        message: 'Installment status toggled successfully',
        severity: 'success'
      });
      fetchEmiInstallments();
    } catch (error) {
      console.error('Error toggling installment status:', error);
      setAlert({
        open: true,
        message: 'Failed to toggle installment status',
        severity: 'error'
      });
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    return formatDateToStandard(dateString);
  };

  // Render tables
  const renderPendingTable = () => {
    if (pendingAP.length === 0) {
      return (
        <Typography variant="body1" sx={{ py: 2, textAlign: 'center' }}>
          No pending accounts payable found.
        </Typography>
      );
    }
    
    return (
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Order ID</TableCell>
              <TableCell>Supplier</TableCell>
              <TableCell>Order Date</TableCell>
              <TableCell>Due Date</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Order Status</TableCell> {/* Renamed from "Status" to "Order Status" */}
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pendingAP.map((item) => {
              const isPastDue = new Date(item.payment_due_date) < new Date();
              
              return (
                <TableRow 
                  key={item.id}
                  sx={{ 
                    backgroundColor: isPastDue ? 'rgba(244, 67, 54, 0.1)' : 'inherit',
                    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.08)' }
                  }}
                >
                  <TableCell>
                    <Button
                      variant="text"
                      onClick={() => navigate(`/${orgId}/orders/${item.id}`)}
                      sx={{ 
                        textTransform: 'none', 
                        p: 0, 
                        minWidth: 'auto',
                        color: 'text.primary',
                        '&:hover': {
                          textDecoration: 'underline',
                          backgroundColor: 'transparent'
                        }
                      }}
                    >
                      {item.id}
                    </Button>
                  </TableCell>
                  <TableCell>
                    {suppliers.find(s => s.id === item.supplier_id)?.supplier_name || item.supplier_id}
                  </TableCell>
                  <TableCell>{formatDate(item.order_date)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {formatDate(item.payment_due_date)}
                      {isPastDue && (
                        <Chip 
                          label="Past Due" 
                          size="small" 
                          color="error" 
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <CurrencyFormatter value={calculateRemainingBalance(item)} />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={item.order_status} 
                      size="small"
                      color={item.order_status === 'Completed' ? 'success' : 'primary'}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => handleOpenPaymentDialog(item)}
                      >
                        Record Payment
                      </Button>
                      <Tooltip title="Mark as Paid">
                        <IconButton 
                          color="success" 
                          onClick={() => handleMarkComplete(item.id)}
                        >
                          <Check />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="View Order">
                        <IconButton 
                          color="info"
                          onClick={() => navigate(`/${orgId}/orders/${item.id}`)}
                        >
                          <Launch />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderCompletedTable = () => {
    return (
      <Box>
        {completedAPLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TanStackTable
            data={completedAP}
            columns={completedAPColumns}
            enablePagination={true}
            pagination={completedAPPagination}
            onPaginationChange={(newPage, newLimit) => {
              setCompletedAPPagination(prev => ({...prev, page: newPage, limit: newLimit}));
              fetchCompletedAP({page: newPage, limit: newLimit});
            }}
            onSearchChange={(searchTerm) => {
              setCompletedAPPagination(prev => ({...prev, page: 1}));
              fetchCompletedAP({page: 1, search: searchTerm});
            }}
          />
        )}
      </Box>
    );
  };

  return (
    <Paper sx={{ p: 3, backgroundColor: '#1e1e1e', color: 'text.primary' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <BackButton />
        <Typography variant="h5" sx={{ ml: 2 }}>
          Accounts Payable
        </Typography>
      </Box>
      
      <Box sx={{ mb: 3 }}>
        <FormControl fullWidth>
          <InputLabel>Filter by Supplier</InputLabel>
          <Select
            value={selectedSupplier}
            onChange={handleSupplierChange}
            label="Filter by Supplier"
          >
            <MenuItem value="">All Suppliers</MenuItem>
            {suppliers.map((supplier) => (
              <MenuItem key={supplier.id} value={supplier.id}>
                {supplier.supplier_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label={`UPFRONT PAYMENTS`} />
          <Tab label={`EMI Payments`} />
          <Tab label={`Payment History`} />
        </Tabs>
      </Box>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          {tabValue === 0 && (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                Unpaid Accounts Payable
              </Typography>
              {renderPendingTable()}
              
              <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
                Paid Accounts Payable
              </Typography>
              {renderCompletedTable()}
            </Box>
          )}
          {tabValue === 1 && (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                EMI Installments
              </Typography>
              {emiInstallments.length === 0 ? (
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                  <Typography color="text.secondary">
                    No EMI installments found for accounts payable
                  </Typography>
                </Paper>
              ) : (
                <Box>
                  {Object.entries(
                    emiInstallments.reduce((acc, installment) => {
                      const orderId = installment.order.id;
                      if (!acc[orderId]) {
                        acc[orderId] = {
                          order: installment.order,
                          installments: []
                        };
                      }
                      acc[orderId].installments.push(installment);
                      return acc;
                    }, {})
                  ).map(([orderId, { order, installments }]) => (
                    <Accordion key={orderId} sx={{ mb: 2 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mr: 2 }}>
                          <Typography 
                            variant="subtitle1" 
                            component="span" 
                            sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                            onClick={() => {
                              const supplierId = order.supplier_id;
                              if (supplierId) {
                                navigate(`/${orgId}/suppliers/${supplierId}`);
                              }
                            }}
                          >
                            Order #{order.id} - {order.supplier?.supplier_name || suppliers.find(s => s.id === order.supplier_id)?.supplier_name || 'N/A'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {installments.length} installments
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <EmiInstallmentTable
                          installments={installments}
                          onStatusUpdate={handleInstallmentStatusUpdate}
                          onTogglePaymentStatus={handleToggleInstallmentPaymentStatus}
                          currencySymbol={symbol}
                        />
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              )}
            </Box>
          )}
          {tabValue === 2 && (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                Payment History
              </Typography>
              {paymentHistoryLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TanStackTable
                  data={paymentHistory}
                  columns={paymentHistoryColumns}
                  enablePagination={true}
                  pagination={paymentHistoryPagination}
                  onPaginationChange={(newPage, newLimit) => {
                    setPaymentHistoryPagination(prev => ({...prev, page: newPage, limit: newLimit}));
                    fetchPaymentHistory({page: newPage, limit: newLimit});
                  }}
                  onSearchChange={(searchTerm) => {
                    setPaymentHistoryPagination(prev => ({...prev, page: 1}));
                    fetchPaymentHistory({page: 1, search: searchTerm});
                  }}
                />
              )}
            </Box>
          )}
        </Box>
      )}
      
      <Dialog open={paymentDialogOpen} onClose={handleClosePaymentDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Record Payment</DialogTitle>
        <DialogContent>
          {selectedOrder && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Order ID: {selectedOrder.id}
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Total Payable: <CurrencyFormatter value={selectedOrder.accounts_payable} />
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Remaining Balance: <CurrencyFormatter value={calculateRemainingBalance(selectedOrder)} />
              </Typography>
              <TextField
                autoFocus
                margin="dense"
                label="Payment Amount"
                type="number"
                fullWidth
                variant="outlined"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                error={paymentAmount && !isValidPayment()}
                helperText={
                  paymentAmount && !isValidPayment()
                    ? `Amount must be between 0 and ${calculateRemainingBalance(selectedOrder).toFixed(2)}`
                    : `Enter amount between 0 and ${calculateRemainingBalance(selectedOrder).toFixed(2)}`
                }
                inputProps={{
                  min: 0,
                  max: calculateRemainingBalance(selectedOrder),
                  step: 0.01
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePaymentDialog}>Cancel</Button>
          <Button 
            onClick={handleSubmitPayment} 
            variant="contained"
            disabled={!isValidPayment()}
          >
            Record Payment
          </Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={() => setAlert({ ...alert, open: false })}
      >
        <Alert 
          onClose={() => setAlert({ ...alert, open: false })} 
          severity={alert.severity}
          sx={{ width: '100%' }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default AccountsPayable;
